import { useEffect, useRef } from 'react';
import { type CaraWorkspaceFieldsFragment, CaraWorkspaceState } from '~/api/graphql';

interface NotificationPreferences {
  enabled: boolean;
  workspaceCompletionIntervalMinutes: number;
}

/**
 * Fires a desktop notification when a workspace transitions out of WORKING state,
 * provided it was working for at least the configured interval.
 *
 * Must be rendered inside the workspace detail view (where the subscription feeds updates).
 */
export function useDesktopNotifications(
  workspace: CaraWorkspaceFieldsFragment | null | undefined,
  preferences: NotificationPreferences | null | undefined
): void {
  const prevStateRef = useRef<CaraWorkspaceState | null>(null);
  const workingStartedAtRef = useRef<number | null>(null);

  useEffect(() => {
    try {
      if (!preferences?.enabled || !workspace || !('Notification' in window)) return;

      const currentState = workspace.state;
      const prevState = prevStateRef.current;

      if (currentState === CaraWorkspaceState.Working && prevState !== CaraWorkspaceState.Working) {
        workingStartedAtRef.current = Date.now();
      }

      if (
        prevState === CaraWorkspaceState.Working &&
        currentState !== CaraWorkspaceState.Working &&
        workingStartedAtRef.current !== null &&
        Notification.permission === 'granted'
      ) {
        const durationMs = Date.now() - workingStartedAtRef.current;
        const thresholdMs = preferences.workspaceCompletionIntervalMinutes * 60 * 1000;

        if (durationMs >= thresholdMs) {
          const workspaceId = workspace.id;
          const notif = new Notification('Workspace completed', {
            body: `"${workspace.title}" has finished.`,
            icon: '/favicon.ico'
          });
          notif.onclick = () => {
            window.focus();
            window.location.href = `/app/copilot/${workspaceId}`;
          };
        }

        workingStartedAtRef.current = null;
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Desktop notification error:', err);
    } finally {
      prevStateRef.current = workspace?.state ?? null;
    }
  }, [
    workspace?.state,
    workspace?.updatedAt,
    preferences?.enabled,
    preferences?.workspaceCompletionIntervalMinutes
  ]);
}
